import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { SubmitHandler, useForm } from 'react-hook-form';

import { BackButton } from '@/components/BackButton';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { hasAllInfoFromQuery } from '@/lib/helpers/urlParams.ts';
import { createPatientFormSchema } from '@/modules/PatientInformationForm/schema';
import { CustomerData } from '@/types/customer.types';

import styles from './PatientInformationForm.module.css';

type PatientInformationFormProps = {
  goBack: () => void;
  callback: (customer: CustomerData) => void;
  requiredFields: string[];
  prefillValues: Partial<CustomerData>;
};

export const PatientInformationForm = ({
  goBack,
  callback,
  requiredFields,
  prefillValues,
}: PatientInformationFormProps) => {
  // Create dynamic schema based on required fields
  const schema = useMemo(() => createPatientFormSchema(requiredFields), [requiredFields]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CustomerData>({
    resolver: zodResolver(schema),
    defaultValues: prefillValues,
    mode: 'onBlur', // Validate on blur for better UX
  });

  useEffect(() => {
    // Pre-fill values from prefillValues
    Object.entries(prefillValues).forEach(([key, value]) => {
      if (value) setValue(key as keyof CustomerData, value);
    });
    if (import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID) {
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname,
        title: 'Patient Information Form',
      });
    }
  }, [prefillValues, setValue]);

  const hasQueryParams = hasAllInfoFromQuery();

  const onSubmit: SubmitHandler<CustomerData> = async (data) => {
    console.log('Submitting customer data:', data);
    await callback(data);
  };

  // Enhanced field definitions with better UX
  const fieldMeta: Record<
    string,
    {
      label: string;
      type: string;
      name: keyof CustomerData;
      placeholder: string;
      autocomplete?: string;
      description?: string;
    }
  > = {
    name: {
      label: 'Full Name',
      type: 'text',
      name: 'name',
      placeholder: 'Enter your first and last name',
      autocomplete: 'name',
      description: 'Please provide your legal first and last name',
    },
    email: {
      label: 'Email Address',
      type: 'email',
      name: 'email',
      placeholder: 'Enter your email address',
      autocomplete: 'email',
      description: "We'll use this to send you appointment confirmations",
    },
    phone: {
      label: 'Phone Number',
      type: 'tel',
      name: 'phone',
      placeholder: 'Enter your phone number',
      autocomplete: 'tel',
      description: 'We may call or text you about your appointment',
    },
    date_of_birth: {
      label: 'Date of Birth',
      type: 'date',
      name: 'date_of_birth',
      placeholder: 'YYYY-MM-DD',
      autocomplete: 'bday',
      description: 'This helps us provide age-appropriate care',
    },
  };

  return (
    <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)} noValidate>
      <span className={styles.formTitle}>
        {hasQueryParams
          ? 'Please check and fill your information'
          : 'One last step, your patient information'}
      </span>

      <div className={styles.formContent}>
        {requiredFields.map((field) => {
          // Map API field to form field (handle 'date' -> 'date_of_birth' mapping)
          const meta = fieldMeta[field];
          if (!meta) {
            console.warn(`Unknown field: ${field}`);
            return null;
          }

          const prefillValue = prefillValues[meta.name];
          const isRequired = requiredFields.includes(field);

          return (
            <FormField
              key={meta.name}
              autoComplete={meta.autocomplete}
              label={meta.label}
              type={meta.type}
              placeholder={meta.placeholder}
              name={meta.name}
              register={register}
              error={errors[meta.name]}
              initValue={prefillValue}
              isRequired={isRequired}
            />
          );
        })}
      </div>

      <div className={styles.formBtnContainer}>
        <BackButton onClick={goBack} />
        <Button className={styles.formSubmitBtn} type="submit">
          Create Appointment
        </Button>
      </div>
    </form>
  );
};
